import React, { useState, useEffect } from 'react';
import { agentApi } from '../../services/api';
import { 
  CpuChipIcon, 
  PlusIcon, 
  ArrowPathIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  PlayIcon,
  PauseIcon,
  TrashIcon,
  SparklesIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  UserGroupIcon,
  EyeIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const AGENT_TYPES = [
  { 
    id: 'contract-scout', 
    name: 'Contract Scout', 
    description: 'Finds and analyzes contract opportunities',
    icon: DocumentTextIcon,
    color: 'bg-blue-500'
  },
  { 
    id: 'email-agent', 
    name: 'Email Agent', 
    description: 'Processes and extracts data from emails',
    icon: EnvelopeIcon,
    color: 'bg-green-500'
  },
  { 
    id: 'vendor-matcher', 
    name: 'Vendor Matcher', 
    description: 'Matches opportunities with qualified vendors',
    icon: UserGroupIcon,
    color: 'bg-purple-500'
  },
  { 
    id: 'monitor-agent', 
    name: 'Monitor Agent', 
    description: 'Monitors contracts for changes and alerts',
    icon: EyeIcon,
    color: 'bg-orange-500'
  },
  { 
    id: 'compliance-agent', 
    name: 'Compliance Agent', 
    description: 'Checks proposals for compliance requirements',
    icon: ShieldCheckIcon,
    color: 'bg-red-500'
  },
  { 
    id: 'analyst-agent', 
    name: 'Analyst Agent', 
    description: 'Provides market analysis and insights',
    icon: ChartBarIcon,
    color: 'bg-indigo-500'
  },
];

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: ClockIcon, color: 'text-yellow-600', bgColor: 'bg-yellow-50' },
  running: { label: 'Running', icon: PlayIcon, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  completed: { label: 'Completed', icon: CheckCircleIcon, color: 'text-green-600', bgColor: 'bg-green-50' },
  failed: { label: 'Failed', icon: XCircleIcon, color: 'text-red-600', bgColor: 'bg-red-50' },
  cancelled: { label: 'Cancelled', icon: PauseIcon, color: 'text-gray-600', bgColor: 'bg-gray-50' },
};

interface AgentTask {
  id: string;
  agentType: string;
  status: keyof typeof STATUS_CONFIG;
  progress: number;
  payload: any;
  result?: any;
  retryCount: number;
  createdAt: string;
  completedAt?: string;
}

const Agents: React.FC = () => {
  const [tasks, setTasks] = useState<AgentTask[]>([]);
  const [agentTypes, setAgentTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState<any>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<AgentTask | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'running' | 'completed' | 'failed'>('all');
  const [newTask, setNewTask] = useState({
    agentType: 'contract-scout',
    payload: '{}',
    priority: 'normal'
  });

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await agentApi.getMyTasks({ limit: 50 });
      setTasks(response.data.tasks || []);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentTypes = async () => {
    try {
      const response = await agentApi.getTypes();
      setAgentTypes(response.data.agents || []);
    } catch (error) {
      console.error('Failed to load agent types');
    }
  };

  const fetchHealth = async () => {
    try {
      const response = await agentApi.getHealth();
      // API returns { success: true, data: { openclaw: {...}, queues: {...} } }
      const healthData = response.data?.data?.openclaw;
      console.log('Health response:', response.data);
      console.log('OpenClaw health:', healthData);
      setHealth(healthData || { status: 'unavailable' });
    } catch (error) {
      console.error('Health fetch error:', error);
      setHealth({ status: 'unavailable' });
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchAgentTypes();
    fetchHealth();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchTasks, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleCreateTask = async () => {
    try {
      const payload = JSON.parse(newTask.payload);
      await agentApi.createTask({
        agentType: newTask.agentType,
        payload,
        priority: newTask.priority
      });
      toast.success('Task created successfully');
      setShowCreateModal(false);
      setNewTask({ agentType: 'contract-scout', payload: '{}', priority: 'normal' });
      fetchTasks();
    } catch (error: any) {
      if (error.message === 'Unexpected token') {
        toast.error('Invalid JSON in payload');
      } else {
        toast.error('Failed to create task');
      }
    }
  };

  const handleCancelTask = async (id: string) => {
    try {
      await agentApi.cancelTask(id);
      toast.success('Task cancelled');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to cancel task');
    }
  };

  const getAgentInfo = (typeId: string) => {
    return AGENT_TYPES.find(a => a.id === typeId) || { 
      name: typeId, 
      icon: CpuChipIcon, 
      color: 'bg-gray-500' 
    };
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getDuration = (created: string, completed?: string) => {
    const start = new Date(created).getTime();
    const end = completed ? new Date(completed).getTime() : Date.now();
    const minutes = Math.floor((end - start) / 60000);
    if (minutes < 1) return '< 1 min';
    if (minutes === 1) return '1 min';
    if (minutes < 60) return `${minutes} mins`;
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 hour';
    return `${hours} hours`;
  };

  const filteredTasks = tasks.filter(task => 
    filter === 'all' || task.status === filter
  );

  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    running: tasks.filter(t => t.status === 'running').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    failed: tasks.filter(t => t.status === 'failed').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Agents</h1>
          <p className="text-sm text-gray-500">Manage and monitor OpenClaw AI agent tasks</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { fetchTasks(); fetchHealth(); }}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
          >
            <ArrowPathIcon className="h-5 w-5" />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
          >
            <PlusIcon className="h-5 w-5" />
            New Task
          </button>
        </div>
      </div>

      {/* Health Status */}
      <div className={`p-4 rounded-lg border ${
        health?.status === 'healthy' ? 'bg-green-50 border-green-200' :
        health?.status === 'ui_only' ? 'bg-blue-50 border-blue-200' :
        health?.status === 'degraded' ? 'bg-yellow-50 border-yellow-200' :
        'bg-red-50 border-red-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${
              health?.status === 'healthy' ? 'bg-green-500 animate-pulse' :
              health?.status === 'ui_only' ? 'bg-blue-500' :
              health?.status === 'degraded' ? 'bg-yellow-500' :
              'bg-red-500'
            }`} />
            <div>
              <p className="font-medium text-gray-900">
                {health?.status === 'healthy' ? 'OpenClaw is online' : 
                 health?.status === 'ui_only' ? 'OpenClaw UI Mode' :
                 health?.status === 'degraded' ? 'OpenClaw is degraded' : 
                 'OpenClaw is offline'}
              </p>
              {health?.status === 'healthy' ? (
                <p className="text-sm text-green-700">
                  WebSocket Gateway v{health.version} • {health.agents?.length || 0} agents available
                </p>
              ) : health?.status === 'ui_only' ? (
                <p className="text-sm text-blue-700">Running in standalone UI mode (API unavailable)</p>
              ) : (
                <p className="text-sm text-gray-600">
                  {health?.message || 'Unable to connect to OpenClaw'}
                </p>
              )}
            </div>
          </div>
          <button 
            onClick={fetchHealth}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <p className="text-sm text-gray-500">Total Tasks</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <p className="text-sm text-yellow-700">Pending</p>
          <p className="text-2xl font-bold text-yellow-900">{stats.pending}</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-700">Running</p>
          <p className="text-2xl font-bold text-blue-900">{stats.running}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <p className="text-sm text-green-700">Completed</p>
          <p className="text-2xl font-bold text-green-900">{stats.completed}</p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <p className="text-sm text-red-700">Failed</p>
          <p className="text-2xl font-bold text-red-900">{stats.failed}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        {(['all', 'pending', 'running', 'completed', 'failed'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              filter === f 
                ? 'bg-brand-600 text-white' 
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Agent Types */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Agents</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {AGENT_TYPES.map(agent => (
            <div key={agent.id} className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:border-brand-300 transition-colors">
              <div className={`${agent.color} p-2 rounded-lg`}>
                <agent.icon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{agent.name}</h3>
                <p className="text-sm text-gray-500">{agent.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tasks List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Tasks</h2>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <ArrowPathIcon className="h-8 w-8 animate-spin text-brand-600" />
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="text-center py-12">
            <CpuChipIcon className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No tasks found</h3>
            <p className="mt-1 text-gray-500">
              {filter === 'all' ? 'Create a new task to get started' : `No ${filter} tasks`}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredTasks.map(task => {
              const agent = getAgentInfo(task.agentType);
              const status = STATUS_CONFIG[task.status];
              const StatusIcon = status.icon;
              
              return (
                <div 
                  key={task.id} 
                  className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => setSelectedTask(task)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`${agent.color} p-2 rounded-lg`}>
                        <agent.icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900">{agent.name}</h3>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.color}`}>
                            <StatusIcon className="h-3 w-3" />
                            {status.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Created {formatTime(task.createdAt)}
                          {task.completedAt && ` • Duration: ${getDuration(task.createdAt, task.completedAt)}`}
                        </p>
                        
                        {/* Progress bar for running tasks */}
                        {task.status === 'running' && (
                          <div className="mt-2">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-brand-600 h-2 rounded-full transition-all duration-500"
                                  style={{ width: `${task.progress}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-600">{task.progress}%</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Retry count */}
                        {task.retryCount > 0 && (
                          <p className="text-xs text-orange-600 mt-1">
                            Retries: {task.retryCount}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {task.status === 'running' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleCancelTask(task.id); }}
                          className="p-2 text-gray-400 hover:text-red-600"
                        >
                          <XCircleIcon className="h-5 w-5" />
                        </button>
                      )}
                      <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Create Agent Task</h2>
              <button onClick={() => setShowCreateModal(false)}>
                <XMarkIcon className="h-6 w-6 text-gray-400" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Agent Type</label>
                <select
                  value={newTask.agentType}
                  onChange={(e) => setNewTask({ ...newTask, agentType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {AGENT_TYPES.map(agent => (
                    <option key={agent.id} value={agent.id}>{agent.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={newTask.priority}
                  onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payload (JSON)</label>
                <textarea
                  value={newTask.payload}
                  onChange={(e) => setNewTask({ ...newTask, payload: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                  placeholder='{"opportunityId": "123", "action": "analyze"}'
                />
                <p className="text-xs text-gray-500 mt-1">Enter valid JSON payload for the agent</p>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTask}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
              >
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-3">
                {(() => {
                  const agent = getAgentInfo(selectedTask.agentType);
                  const AgentIcon = agent.icon;
                  return (
                    <div className={`${agent.color} p-2 rounded-lg`}>
                      <AgentIcon className="h-5 w-5 text-white" />
                    </div>
                  );
                })()}
                <div>
                  <h2 className="text-lg font-semibold">{getAgentInfo(selectedTask.agentType).name}</h2>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[selectedTask.status].bgColor} ${STATUS_CONFIG[selectedTask.status].color}`}>
                    {STATUS_CONFIG[selectedTask.status].label}
                  </span>
                </div>
              </div>
              <button onClick={() => setSelectedTask(null)}>
                <XMarkIcon className="h-6 w-6 text-gray-400" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh] space-y-4">
              {/* Progress */}
              {selectedTask.status === 'running' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Progress</label>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-brand-600 h-3 rounded-full transition-all"
                        style={{ width: `${selectedTask.progress}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">{selectedTask.progress}%</span>
                  </div>
                </div>
              )}
              
              {/* Timing */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Created</label>
                  <p className="text-sm text-gray-600">{formatTime(selectedTask.createdAt)}</p>
                </div>
                {selectedTask.completedAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Completed</label>
                    <p className="text-sm text-gray-600">{formatTime(selectedTask.completedAt)}</p>
                  </div>
                )}
              </div>
              
              {/* Payload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payload</label>
                <pre className="bg-gray-50 p-3 rounded-lg text-xs overflow-auto max-h-40">
                  {JSON.stringify(selectedTask.payload, null, 2)}
                </pre>
              </div>
              
              {/* Result */}
              {selectedTask.result && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Result</label>
                  <pre className="bg-green-50 p-3 rounded-lg text-xs overflow-auto max-h-60">
                    {JSON.stringify(selectedTask.result, null, 2)}
                  </pre>
                </div>
              )}
              
              {/* Error */}
              {selectedTask.status === 'failed' && (
                <div>
                  <label className="block text-sm font-medium text-red-700 mb-1">Error</label>
                  <div className="bg-red-50 p-3 rounded-lg text-sm text-red-800">
                    Task failed after {selectedTask.retryCount} retries
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-between">
              {selectedTask.status === 'running' && (
                <button
                  onClick={() => { handleCancelTask(selectedTask.id); setSelectedTask(null); }}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  Cancel Task
                </button>
              )}
              <button
                onClick={() => setSelectedTask(null)}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 ml-auto"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agents;
